import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { buildApiUrl } from '../config/apiConfig';

/**
 * AuthCallback - Google OAuth callback handler
 * 
 * Fixed Issues:
 * - Better error handling
 * - Proper state management
 * - Forces AuthContext refresh after successful auth
 * - Clear error messages
 */

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      // Extract session_id from URL fragment
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        console.error('❌ No session_id in URL fragment');
        toast.error('Authentication failed: No session ID');
        setTimeout(() => navigate('/login', { replace: true }), 1500);
        return;
      }

      console.log('🔄 Processing session_id:', sessionId.substring(0, 20) + '...');

      try {
        // Exchange session_id for user data and session_token
        const response = await fetch(buildApiUrl('/auth/google/session'), {
          method: 'POST',
          headers: {
            'X-Session-ID': sessionId,
            'Content-Type': 'application/json'
          },
          credentials: 'include' // Important: Send/receive cookies
        });

        console.log('📡 Session exchange status:', response.status);

        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Authentication failed' }));
          console.error('❌ Session exchange error:', error);
          throw new Error(error.detail || 'Authentication failed');
        }

        const data = await response.json();
        console.log('✅ Session exchange successful, user:', data.user?.username);
        
        // Store auth flag to help AuthContext know OAuth just completed
        sessionStorage.setItem('oauth_completed', 'true');
        sessionStorage.setItem('oauth_user', JSON.stringify(data.user));
        
        // Success toast
        toast.success(`Welcome back, ${data.user.username || data.user.email}!`);
        
        // Small delay to ensure cookie is set
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Force page reload to trigger AuthContext refresh
        // This ensures the session cookie is picked up
        console.log('🔄 Redirecting to homepage...');
        window.location.href = '/';
        
      } catch (error) {
        console.error('❌ OAuth error:', error);
        toast.error(error.message || 'Authentication failed. Please try again.');
        
        // Clear any partial state
        sessionStorage.removeItem('oauth_completed');
        sessionStorage.removeItem('oauth_user');
        
        // Redirect to login after showing error
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      }
    };

    processSession();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
      <div className="text-center">
        <Loader className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
        <p className="text-gray-700 text-lg font-medium">Completing authentication...</p>
        <p className="text-gray-500 text-sm mt-2">Please wait, this will only take a moment</p>
      </div>
    </div>
  );
};

export default AuthCallback;
